import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import './BlogList.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'de');
  const t = translations[language];
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, [page, language]);
  
  const toggleLanguage = () => {
    const newLang = language === 'de' ? 'en' : 'de';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
    setPage(1); // Reset to first page when changing language
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/blog?page=${page}&limit=10`);
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      const data = await response.json();
      // Filter posts by language on frontend (temporary workaround for mixed language field)
      const filteredPosts = data.posts.filter(post => {
        // If post doesn't have language field, try to detect from title/content
        if (!post.language) {
          // Check if title contains German characters or German words
          const germanPattern = /ä|ö|ü|ß|Geschichten|für|und/i;
          const isGerman = germanPattern.test(post.title);
          return language === 'de' ? isGerman : !isGerman;
        }
        return post.language === language;
      });
      setPosts(filteredPosts);
      setTotalPages(Math.ceil(filteredPosts.length / 10));
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="blog-list-container">
        <div className="blog-header">
          <Button onClick={() => navigate('/dashboard')} className="back-button">
            ← {language === 'de' ? 'Zurück' : 'Back'}
          </Button>
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'de' ? '🇬🇧 English' : '🇩🇪 Deutsch'}
          </button>
          <h1>{t.blogTitle}</h1>
          <p>{t.blogSubtitle}</p>
        </div>
        <div className="loading">Loading...</div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-list-container">
        <div className="blog-header">
          <Button onClick={() => navigate('/dashboard')} className="back-button">
            ← {language === 'de' ? 'Zurück' : 'Back'}
          </Button>
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'de' ? '🇬🇧 English' : '🇩🇪 Deutsch'}
          </button>
          <h1>{t.blogTitle}</h1>
          <p>{t.blogSubtitle}</p>
        </div>
        <div className="error-message">{error}</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-list-container">
      <div className="blog-header">
        <Button onClick={() => navigate('/dashboard')} className="back-button">
          ← {language === 'de' ? 'Zurück' : 'Back'}
        </Button>
        <button onClick={toggleLanguage} className="language-toggle-btn">
          {language === 'de' ? '🇬🇧 English' : '🇩🇪 Deutsch'}
        </button>
        <h1>{t.blogTitle}</h1>
        <p>{t.blogSubtitle}</p>
      </div>

      {posts.length === 0 ? (
        <div className="no-posts">
          <p>{t.noBlogPosts}</p>
        </div>
      ) : (
        <>
          <div className="blog-grid">
            {posts.map((post) => (
              <div key={post.id} className="blog-card" onClick={() => navigate(`/blog/${post.id}`)}>
                {post.featured_image && (
                  <div className="blog-card-image">
                    <img src={post.featured_image} alt={post.title} />
                  </div>
                )}
                <div className="blog-card-content">
                  <h2>{post.title}</h2>
                  <div className="blog-card-meta">
                    <span className="author">{post.author_name}</span>
                    <span className="date">{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="blog-excerpt">
                    {truncateText(stripHtml(post.content), 150)}
                  </p>
                  <button className="read-more-btn">{t.readMore}</button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
              >
                ← Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <Footer />
    </div>
  );
}

export default BlogList;
